import { Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { GameWithStatus } from '@/api/learnerGames'
import { formatUnlockHint } from '@/lib/playground/formatUnlockHint'
import { cn } from '@/lib/utils'

const KIDS_TAG_PATTERN = /\[Kids\]/i

function getDisplayTitle(title: string): string {
  return title.replace(KIDS_TAG_PATTERN, '').replace(/\s{2,}/g, ' ').trim()
}

export interface GameCardProps {
  game: GameWithStatus
  onClick: (game: GameWithStatus) => void
  className?: string
}

export function GameCard({ game, onClick, className }: GameCardProps) {
  const { t } = useTranslation('learner')
  const hint = formatUnlockHint(game.unlockCondition, t)
  const typeLabel = game.type ? t(`playground.card.type.${game.type}`) : null
  const isEasyMode = KIDS_TAG_PATTERN.test(game.title)
  const displayTitle = getDisplayTitle(game.title)

  const ariaLabel = game.isUnlocked
    ? t('playground.card.unlockedAria', { title: displayTitle })
    : t('playground.card.lockedAria', { title: displayTitle, lessonRef: hint })

  return (
    <button
      type="button"
      onClick={() => onClick(game)}
      aria-label={ariaLabel}
      className={cn(
        'group relative flex w-full flex-col overflow-hidden rounded-2xl border border-lc-v3-gray-300 bg-lc-bg-primary text-left shadow-[0_4px_12px_0_rgb(0_0_0_/_0.03)]',
        'transition hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-lc-key-500',
        game.isUnlocked && 'hover:-translate-y-0.5',
        !game.isUnlocked && 'opacity-60 cursor-not-allowed',
        className,
      )}
    >
      <div className="relative h-32 w-full overflow-hidden bg-lc-v3-gray-100">
        {game.imageUrl ? (
          <img
            src={game.imageUrl}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : null}
        {!game.isUnlocked ? (
          <div
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center bg-black/40"
          >
            <Lock className="size-8 text-white" />
          </div>
        ) : null}
        {isEasyMode ? (
          <span className="absolute right-2 top-2 rounded-md bg-lc-bg-overlay px-2 py-1 text-lc-v3-caption-2 font-bold text-lc-text-inverse">
            {t('playground.card.easyMode')}
          </span>
        ) : null}
      </div>

      <div className="flex w-full flex-col items-start gap-3 p-5">
        {typeLabel ? (
          <span className="inline-block rounded-md bg-[#ebf3ff] px-2 py-1 text-lc-v3-caption-2 font-bold text-lc-key-500">
            {typeLabel}
          </span>
        ) : null}

        <div className="flex w-full flex-col items-start gap-1.5">
          <h3 className="line-clamp-1 w-full text-[18px] font-bold leading-[1.4] text-lc-v3-gray-900">
            {displayTitle}
          </h3>

          {game.isUnlocked ? (
            game.description ? (
              <p className="line-clamp-2 w-full text-[13px] font-medium leading-[1.5] text-lc-v3-gray-700">
                {game.description}
              </p>
            ) : null
          ) : (
            <p className="line-clamp-2 w-full text-[13px] font-medium leading-[1.5] text-lc-v3-gray-700">
              {t('playground.card.lockedHint', { lessonRef: hint })}
            </p>
          )}
        </div>
      </div>
    </button>
  )
}
